import Auth from '@aws-amplify/auth';
import { Signer } from '@aws-amplify/core';
import * as urlLib from 'url';
import log from 'loglevel';
import _ from 'lodash';
import fetch from 'isomorphic-fetch';
import * as config from '../config';

const ENV_CONFIG = config.get();

type BackendExtension = {
  code: string;
};

type BackendLocation = {
  column: number;
  line: number;
};

type BackendError = {
  message: string;
  path: string[];
  extensions: BackendExtension;
  locations: BackendLocation[];
};

export class AcornGraphqlError extends Error {
  errors: BackendError[];
  data: any;
  constructor({ data, errors }) {
    super(errors[0]?.extensions?.code || 'UNKNOWN_ERROR');
    this.name = 'AcornGraphqlError';
    this.errors = errors;
    this.data = data;
  }
  toString() {
    return _.map(
      this.errors,
      ({ message, extensions }: BackendError) =>
        `${extensions?.code}: ${message}`,
    ).join(', ');
  }
}
/*
// Uses Amplify library to make authenticated requests to backend
export const fetchData = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: RequestInit['headers']
): (() => Promise<TData>) => {
  return async () => {
    const result = (await API.graphql(
      graphqlOperation(query, variables || {})
    )) as any;
    return result.data;
  };
};
*/

// makes graphql API request with query being a string
// This is a little different because we're running API Gateway behind Cloudfront and we're using AWS_IAM authentication
// The request needs to be signed with the Hostname of the API Gateway host, but then dispatched to Cloudfront.
export const fetchFromGraphql = async (query: string, variables) => {
  const signedRequest = await signRequest(ENV_CONFIG.grahpqlApiCdnSigningHost, {
    query,
    variables,
  });
  delete signedRequest.headers.host;
  const result = await fetch(ENV_CONFIG.graphqlApiCdn, {
    method: 'POST',
    headers: { ...signedRequest.headers, 'Content-Type': 'application/json' },
    body: signedRequest.data,
  });
  if (!result.ok) {
    log.warn({ result }, 'Non-ok result code from fetch call');
    throw new Error(
      `Unexpected response code from server: ${result.status}: ${result.statusText}.`,
    );
  }

  const bodyJson = await result.json();
  if ('errors' in bodyJson) {
    log.warn(
      { bodyJson },
      'fetchFromGraphql: got errors in Graphql response from server',
    );
    throw new AcornGraphqlError(bodyJson);
  }
  return bodyJson;
};

export const fetchData = <TData, TVariables>(
  query: string,
  variables?: TVariables,
  options?: RequestInit['headers'],
): (() => Promise<TData>) => {
  return async () => {
    const result = await fetchFromGraphql(query, variables);
    return result.data;
  };
};

const signRequest = async (url, data) => {
  const { search, ...parsedUrl } = urlLib.parse(url, true, true);
  const formattedUrl = urlLib.format({
    ...parsedUrl,
    query: { ...parsedUrl.query },
  });
  const credentials = await Auth.currentCredentials();
  const essentialCredentials = await Auth.essentialCredentials(credentials);
  let params = {
    headers: {},
    data: JSON.stringify(data),
    method: 'POST',
    url: formattedUrl,
  };
  const cred = {
    secret_key: essentialCredentials.secretAccessKey,
    access_key: essentialCredentials.accessKeyId,
    session_token: essentialCredentials.sessionToken,
  };
  // set your region and service here
  const serviceInfo = {
    region: ENV_CONFIG.region,
    service: 'execute-api',
  };
  return await Signer.sign(params, cred, serviceInfo);
};
